import React, { useState, useContext, useEffect } from 'react';
import styles from './styles/CreateKey.module.css';
import keysUi from './keys/styles/Keys.module.css';
import {
    FaEdit,
    FaTrash,
    FaMinus,
    FaPlus,
    FaChevronDown,
    FaChevronUp,
    FaExclamationTriangle,
} from 'react-icons/fa';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import AuthContext from '../../../context/AuthContext';
import { hashStringToColor } from '../../../utils/collabColors';
import useIsMobile from '../../../utils/useIsMobile';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';
import { KEY_VISUALS } from './keys/keyVisuals';
import { KeyRoleIcon } from './keys/KeyBadge';
import KeyLegend from './keys/KeyLegend';
import KeyUsageChip from './keys/KeyUsageChip';
import { KEY_WARNINGS, keyUsage, keyWarnings } from './keys/keyGraph';

const WARNING_TEXT = {
    [KEY_WARNINGS.NEVER_GRANTED]: 'No situation or choice gives this key yet.',
    [KEY_WARNINGS.NEVER_USED]: "This key doesn't affect any choice yet.",
};

/**
 * The Keys panel: create keys, and see at a glance where each one is picked up
 * and which choices depend on it. Every situation or choice listed is a link to
 * its Keys tab, and expanding a key spotlights it on the canvas.
 */
const CreateKey = ({ setRenderEditSidebar }) => {

    const {
        nodes,
        edges,
        keys,
        getKey,
        getEdge,
        createKey,
        updateKey,
        deleteKey,
        centerNodeInView,
    } = useCanvas();
    const {
        selectItem,
        clearSelection,
        selectedKeyId,
        selectedKeyQueue,
        setActiveEditTab,
        setHighlightedKeyId,
    } = useEditorUI();
    const { userId: authUserId } = useContext(AuthContext);
    const isMobile = useIsMobile();

    const [mode, setMode] = useState('view');
    const [keyFormData, setKeyFormData] = useState({ name: '', description: '' });
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [errors, setErrors] = useState({});
    // Someone with no keys yet is the person who most needs the explanation.
    const [showHelp, setShowHelp] = useState(() => keys.length === 0);

    // Selection is an id, so the form is seeded from the current key rather than
    // from a copy captured at the moment it was selected.
    useEffect(() => {
        const key = selectedKeyId ? getKey(selectedKeyId) : null;
        if (key) {
            setKeyFormData({ name: key.name || '', description: key.description || '' });
        } else {
            setKeyFormData({ name: '', description: '' });
            setMode('view');
        }
        // Keyed on the id alone: a key edit landing for THIS key must not
        // reseed the form out from under whoever is typing in it.
    }, [selectedKeyId]);

    // The spotlight belongs to this panel; closing or switching panels ends it.
    useEffect(() => () => setHighlightedKeyId(null), [setHighlightedKeyId]);

    // A key deleted by anyone drops out of the expanded list.
    useEffect(() => {
        const live = new Set(keys.map((key) => String(key.id)));
        setExpandedKeys((current) => {
            const next = current.filter((id) => live.has(String(id)));
            return next.length === current.length ? current : next;
        });
    }, [keys]);

    /** A nameless key is unidentifiable in every list that offers it, so require one. */
    const validate = () => {
        if (!keyFormData.name.trim()) {
            setErrors({ name: 'Key name is required' });
            return false;
        }
        setErrors({});
        return true;
    };

    const closeForm = () => {
        setMode('view');
        setKeyFormData({ name: '', description: '' });
        setErrors({});
        clearSelection();
    };

    const expandKey = (keyId) => {
        setExpandedKeys((current) => (current.includes(keyId) ? current : [...current, keyId]));
        setHighlightedKeyId(keyId);
    };

    const collapseKey = (keyId) => {
        const remaining = expandedKeys.filter((id) => id !== keyId);
        setExpandedKeys(remaining);
        // Fall back to the most recently expanded key still open, if any.
        setHighlightedKeyId(remaining.length > 0 ? remaining[remaining.length - 1] : null);
    };

    const handleCreateKey = () => {
        if (!validate()) return;
        const createdId = createKey(keyFormData);
        if (createdId) {
            setExpandedKeys([createdId]);
            setHighlightedKeyId(createdId);
        }
        closeForm();
    };

    const handleEditKey = (keyId) => {
        const key = getKey(keyId);
        if (!key) return;
        selectItem('key', keyId);
        setKeyFormData({ name: key.name || '', description: key.description || '' });
        setMode('edit');
    };

    const handleUpdateKey = () => {
        if (!selectedKeyId || !validate()) return;
        updateKey(selectedKeyId, keyFormData);
        closeForm();
    };

    const handleDeleteKey = (keyId) => {
        const confirmed = window.confirm(
            'Delete this key? Choices that depend on it will become visible to everyone.'
        );
        if (!confirmed) return;
        deleteKey(keyId);
    };

    /** Opens a situation or choice on its Keys tab and brings it into view. */
    const jumpTo = (kind, id) => {
        const nodeId = kind === 'situation' ? id : getEdge(id)?.source;
        selectItem(kind, id);
        setActiveEditTab('4');
        if (nodeId) centerNodeInView(nodeId);
        if (isMobile && typeof setRenderEditSidebar === 'function') setRenderEditSidebar(true);
    };

    const renderKeyForm = () => {
        const queue = selectedKeyId ? selectedKeyQueue[selectedKeyId] : null;
        if (queue && queue[0] !== authUserId) {
            return (<div>Waiting</div>)
        }
        return(
        <div className={styles.keyForm}>
            <div className={styles.formGroup}>
                <label>
                    Name <span className={styles.required}>*</span>
                </label>
                <input
                    type="text"
                    value={keyFormData.name}
                    onChange={(e) => setKeyFormData({ ...keyFormData, name: e.target.value })}
                    pattern="[A-Za-z0-9 ]+"
                    placeholder="e.g. Rusty key"
                    required
                />
                {errors.name && <span className={styles.error}>{errors.name}</span>}
            </div>
            <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                    value={keyFormData.description}
                    placeholder="A note for yourself. Players never see this."
                    onChange={(e) => setKeyFormData({ ...keyFormData, description: e.target.value })}
                />
            </div>
            <div className={styles.formActions}>
                {mode === 'create' ? (
                    <>
                        <button onClick={handleCreateKey}>
                            <FaPlus /> Create
                        </button>
                        <button onClick={closeForm}>Cancel</button>
                    </>
                ) : (
                    <>
                        <button onClick={handleUpdateKey}>
                            Save Changes
                        </button>
                        <button onClick={closeForm}>Cancel</button>
                    </>
                )}
            </div>
            {mode === 'edit' && selectedKeyId && getKey(selectedKeyId) && renderKeySections(getKey(selectedKeyId))}
        </div>
        )
    };

    const renderUsageSection = (role, heading, items, emptyText) => (
        <div
            className={keysUi.usageSection}
            style={{ '--role-color': KEY_VISUALS[role].color }}
        >
            <h4 className={keysUi.usageHeading}>
                <KeyRoleIcon role={role} size={12} />
                {heading}
            </h4>
            {items.length > 0 ? (
                <ul className={keysUi.usageList}>
                    {items.map(({ kind, item }) => (
                        <li key={item.id}>
                            <KeyUsageChip
                                kind={kind}
                                id={item.id}
                                title={item.data?.title}
                                onJump={jumpTo}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <p className={keysUi.muted}>{emptyText}</p>
            )}
        </div>
    );

    const renderKeySections = (key) => {
        // Read straight off the draft graph: a node grants a key, an edge grants one
        // or is gated on one. There is no parallel situation list to consult.
        const usage = keyUsage({ nodes, edges }, key.id);
        const warnings = keyWarnings(usage);

        const pickedUpAt = [
            ...usage.grantedByNodes.map((item) => ({ kind: 'situation', item })),
            ...usage.grantedByEdges.map((item) => ({ kind: 'choice', item })),
        ];
        const takenAwayAt = [
            ...usage.removedByNodes.map((item) => ({ kind: 'situation', item })),
            ...usage.removedByEdges.map((item) => ({ kind: 'choice', item })),
        ];
        const choices = (list) => list.map((item) => ({ kind: 'choice', item }));

        return (
            <div className={styles.keySections}>
                {warnings.map((warning) => (
                    <p key={warning} className={keysUi.softWarning}>
                        <FaExclamationTriangle size={11} />
                        {WARNING_TEXT[warning]}
                    </p>
                ))}
                {renderUsageSection('grant', 'Picked up at', pickedUpAt, 'Nothing gives this key yet.')}
                {renderUsageSection(
                    'remove',
                    'Taken away at',
                    takenAwayAt,
                    'Nothing takes this key away.'
                )}
                {renderUsageSection(
                    'has',
                    'Visible only WITH this key',
                    choices(usage.hasEdges),
                    'No choices require this key.'
                )}
                {renderUsageSection(
                    'lacks',
                    'Visible only WITHOUT this key',
                    choices(usage.lacksEdges),
                    'No choices are hidden by this key.'
                )}
            </div>
        );
    };

    const renderExplainer = () => (
        <div className={keysUi.explainer} {...tutorialAttr(TUTORIAL_TARGETS.KEYS_HELP)}>
            <button
                type="button"
                className={keysUi.explainerToggle}
                onClick={() => setShowHelp((open) => !open)}
                aria-expanded={showHelp}
            >
                How keys work
                {showHelp ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
            </button>
            {showHelp && (
                <div className={keysUi.explainerBody}>
                    <ol>
                        <li>
                            Players pick up a key when they arrive at a situation or pick a
                            choice that gives it. Set this on the situation or choice's Keys tab.
                        </li>
                        <li>
                            A situation or choice can also take a key away, so players who get
                            there no longer have it.
                        </li>
                        <li>
                            A choice can be shown only to players who HAVE a key, or only to
                            players who DO NOT have it.
                        </li>
                        <li>
                            Expand a key below to see where it is used. The canvas fades
                            everything else.
                        </li>
                    </ol>
                    <KeyLegend />
                </div>
            )}
        </div>
    );

    return (
        <div className={styles.createKeyContainer} {...tutorialAttr(TUTORIAL_TARGETS.KEYS_PANEL)}>
            {mode === 'view' && (
                <>
                    <button
                        className={styles.createButton}
                        onClick={() => setMode('create')}
                        {...tutorialAttr(TUTORIAL_TARGETS.KEYS_CREATE)}
                    >
                        + New key
                    </button>
                    {renderExplainer()}
                    <div className={styles.keysList}>
                        {keys.map(key => {
                            const isExpanded = expandedKeys.includes(key.id);
                            let colors = [];
                            const selectedUsers = selectedKeyQueue[key.id]
                            if (selectedUsers) {
                                if (selectedUsers.includes(authUserId)) {
                                    colors.push(hashStringToColor(authUserId));
                                } else {
                                    selectedUsers.forEach((userId) => {
                                        colors.push(hashStringToColor(userId));
                                    });
                                }
                            }
                            return (<div
                                key={key.id}
                                className={`${styles.keyItem} ${isExpanded ? styles.expanded : ''} ${colors.length > 0 ? styles.selectedKey : ''}`}
                                style={{'--highlight-color': colors.length > 0 ? colors[0] : undefined}}
                                onClick={() => {
                                    if (!isExpanded) expandKey(key.id);
                                }}
                            >
                                <div className={styles.keyHeader}>
                                    <h3>{key.name}</h3>
                                    {!isExpanded && (
                                        <div className={styles.keyActions}>
                                            <FaEdit
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleEditKey(key.id);
                                                }}
                                                className={styles.icon}
                                                title="Edit key"
                                            />
                                            <FaTrash
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDeleteKey(key.id);
                                                }}
                                                className={styles.icon}
                                                title="Delete key"
                                            />
                                        </div>
                                    )}
                                    {isExpanded && (
                                        <FaMinus
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                collapseKey(key.id);
                                            }}
                                            className={styles.icon}
                                            title="Collapse"
                                        />
                                    )}
                                </div>
                                {/* Description displayed in both states */}
                                <p
                                    className={`${styles.description} ${
                                        isExpanded ? styles.expandedDescription : styles.collapsedDescription
                                    }`}
                                >
                                    {key.description}
                                </p>
                                {isExpanded && (
                                    <div className={styles.keyContent} onClick={(event) => event.stopPropagation()}>
                                        {renderKeySections(key)}
                                        <div className={styles.keyActionsMiddle}>
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleEditKey(key.id);
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDeleteKey(key.id);
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )
                        })}
                    </div>
                </>
            )}
            {(mode === 'create' || mode === 'edit') && renderKeyForm()}
        </div>
    );

}

export default CreateKey
